import { useEffect, useMemo, useState } from 'react';
import { Gem } from 'lucide-react';
import { useSandboxActivateSubscription } from '@/services/mutations/sandbox';
import {
	SandboxField,
	SandboxModal,
	SandboxNotice,
	sandboxSelectClass,
} from './SandboxModal';
import { formatMoney, fullName } from '../sandboxLabels';

interface Props {
	scenarioId: string;
	catalog: Sandbox.Catalog;
	users: Sandbox.ScenarioUser[];
	/** Usuário pré-selecionado (ao abrir pelo card do usuário). */
	initialUserId?: string | null;
	isOpen: boolean;
	onClose: () => void;
}

/** Ativa um plano para o usuário sem passar pelo Stripe (estado pós-pagamento). */
const SandboxSubscriptionModal = ({
	scenarioId,
	catalog,
	users,
	initialUserId,
	isOpen,
	onClose,
}: Props) => {
	const eligibleUsers = useMemo(
		() => users.filter((u) => !u.active_subscription),
		[users],
	);
	const [userId, setUserId] = useState('');
	const [clubId, setClubId] = useState('');

	useEffect(() => {
		if (!isOpen) return;
		setUserId(
			initialUserId && eligibleUsers.some((u) => u.id === initialUserId)
				? initialUserId
				: eligibleUsers[0]?.id ?? '',
		);
		setClubId(catalog.clubs[0]?.id ?? '');
	}, [isOpen, initialUserId, eligibleUsers, catalog.clubs]);

	const { mutate, isPending } = useSandboxActivateSubscription(
		scenarioId,
		onClose,
	);

	const club = catalog.clubs.find((c) => c.id === clubId);

	return (
		<SandboxModal
			isOpen={isOpen}
			onClose={onClose}
			title="Ativar assinatura"
			description="Cria a assinatura ACTIVE com os benefícios do clube, como o webhook do Stripe faria após o checkout. Nenhuma cobrança é feita."
			icon={<Gem className="h-4 w-4" />}
			confirmText="Ativar assinatura"
			onConfirm={() => mutate({ user_id: userId, club_id: clubId })}
			confirmDisabled={!userId || !clubId}
			isPending={isPending}
		>
			<div className="space-y-4 py-2">
				{eligibleUsers.length === 0 ? (
					<SandboxNotice tone="warning">
						Todos os usuários do cenário já têm assinatura ativa
						(uma por usuário). Crie outro usuário.
					</SandboxNotice>
				) : null}
				<SandboxField label="Usuário">
					<select
						className={sandboxSelectClass}
						value={userId}
						onChange={(e) => setUserId(e.target.value)}
					>
						{eligibleUsers.map((u) => (
							<option key={u.id} value={u.id}>
								{fullName(u)} · {u.email}
							</option>
						))}
					</select>
				</SandboxField>
				<SandboxField label="Clube">
					<select
						className={sandboxSelectClass}
						value={clubId}
						onChange={(e) => setClubId(e.target.value)}
					>
						{catalog.clubs.map((c) => (
							<option key={c.id} value={c.id}>
								{c.name} · {formatMoney(c.price)}/mês
								{c.shelf ? ` · ${c.shelf}` : ''}
							</option>
						))}
					</select>
				</SandboxField>
				{club ? (
					<div className="rounded-2xl border border-stone-100 bg-stone-50/60 p-4">
						<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
							Benefícios que o usuário recebe
						</p>
						{club.benefits.length === 0 ? (
							<p className="mt-2 text-xs text-rose-600">
								Este clube não tem benefícios ativos; a
								assinatura seria vazia.
							</p>
						) : (
							<ul className="mt-2 space-y-1">
								{club.benefits.map((b) => (
									<li
										key={b.id}
										className="text-xs text-stone-600"
									>
										• {b.service_name}: {b.default_amount}{' '}
										uso(s) com{' '}
										{b.service_discount_percentage}% de
										desconto
									</li>
								))}
							</ul>
						)}
					</div>
				) : null}
			</div>
		</SandboxModal>
	);
};

export { SandboxSubscriptionModal };
